import {
	isValidValue,
	parseNumber,
	processMatchMode,
	shouldRenderAsNumber,
	toArray,
} from 'react-querybuilder'

import { canonicalizeField, keywordSuffix } from '../utils/prefixer'

import type {
	BoolQuery,
	QueryClause,
	RangeRule,
	WildcardClause,
} from '../types/query'
import type { RuleProcessor } from 'react-querybuilder'

/**
 * Rule processor for OpenSearch DSL.
 *
 * Differences from a hand-written processor:
 * - Canonical field prefixing happens once via `canonicalizeField`.
 *   `@timestamp` and `normalized_fields.*` flow through untouched; raw
 *   fields become `event.<raw>`.
 * - `.keyword` suffix is appended by the clause emitter (not the prefixer)
 *   so range/exists queries don't accidentally get `.keyword` glued on.
 */

const RANGE_OP_MAP: Record<string, 'gt' | 'gte' | 'lt' | 'lte'> = {
	'>': 'gt',
	'>=': 'gte',
	'<': 'lt',
	'<=': 'lte',
}

function renderRuleValue(value: unknown, parseNumbers?: unknown): unknown {
	if (typeof value !== 'string') return value
	if (parseNumbers === false) return value
	const num = Number(value)
	if (Number.isFinite(num) && value.trim() !== '') return num
	return value
}

function wildcard(field: string, pattern: string): WildcardClause {
	return {
		wildcard: {
			[field]: { value: pattern, case_insensitive: true },
		},
	}
}

function negate(clause: QueryClause): BoolQuery {
	return { bool: { must_not: [clause] } }
}

/**
 * Convert a react-querybuilder rule into a DSL clause.
 *
 * @param rule - RQB rule (field/operator/value)
 * @param options - RQB options (parseNumbers, preserveValueOrder)
 * @returns A clause, or `false` when the rule is invalid (per RQB convention).
 */
export const ruleProcessor: RuleProcessor = (
	rule,
	options = {},
): QueryClause | false => {
	if (processMatchMode(rule)) return false
	// Empty/placeholder field — drop the rule. Prevents malformed DSL like
	// `{term: {"event..keyword": ...}}` when the user hasn't picked a field.
	if (!rule.field) return false

	const { operator, value } = rule
	const field = canonicalizeField(rule.field)
	const parseNumbers = options.parseNumbers
	const op = operator.toLowerCase()

	switch (op) {
		case '=':
			return {
				term: {
					[keywordSuffix(field)]: renderRuleValue(value, parseNumbers),
				},
			}

		case '!=':
			return negate({
				term: {
					[keywordSuffix(field)]: renderRuleValue(value, parseNumbers),
				},
			})

		case 'null':
			return negate({ exists: { field } })

		case 'notnull':
			return { exists: { field } }

		case '>':
		case '>=':
		case '<':
		case '<=':
			return {
				range: {
					[field]: {
						[RANGE_OP_MAP[op]]: renderRuleValue(value, parseNumbers),
					} as RangeRule,
				},
			}

		case 'between':
		case 'notbetween': {
			const values = toArray(value)
			if (
				values.length < 2 ||
				!isValidValue(values[0]) ||
				!isValidValue(values[1])
			) {
				return false
			}
			let [first, second] = values
			const allowNumbers = parseNumbers !== false
			if (
				shouldRenderAsNumber(first, allowNumbers) &&
				shouldRenderAsNumber(second, allowNumbers)
			) {
				const firstNum = parseNumber(first, { parseNumbers: allowNumbers })
				const secondNum = parseNumber(second, { parseNumbers: allowNumbers })
				if (!options.preserveValueOrder && secondNum < firstNum) {
					first = secondNum
					second = firstNum
				} else {
					first = firstNum
					second = secondNum
				}
			}
			const clause: QueryClause = {
				range: { [field]: { gte: first, lte: second } },
			}
			return op === 'notbetween' ? negate(clause) : clause
		}

		case 'in':
		case 'notin': {
			const values = toArray(value)
				.map((v) => renderRuleValue(v, parseNumbers))
				.filter((v) => v !== '' && v !== null && v !== undefined)
			if (values.length === 0) return false
			const clause: QueryClause = {
				terms: { [keywordSuffix(field)]: values },
			}
			return op === 'notin' ? negate(clause) : clause
		}

		case 'contains':
		case 'doesnotcontain': {
			if (typeof value !== 'string' || value === '') return false
			const clause = wildcard(field, `*${value}*`)
			return op === 'doesnotcontain' ? negate(clause) : clause
		}

		case 'beginswith':
		case 'doesnotbeginwith': {
			if (typeof value !== 'string' || value === '') return false
			const clause = wildcard(field, `${value}*`)
			return op === 'doesnotbeginwith' ? negate(clause) : clause
		}

		case 'endswith':
		case 'doesnotendwith': {
			if (typeof value !== 'string' || value === '') return false
			const clause = wildcard(field, `*${value}`)
			return op === 'doesnotendwith' ? negate(clause) : clause
		}

		// Full-text match on the analyzed field (no `.keyword`). `like` is the
		// loose/fuzzy counterpart to `=`.
		case 'like':
		case 'notlike': {
			if (typeof value !== 'string' || value === '') return false
			const clause: QueryClause = { match: { [field]: value } }
			return op === 'notlike' ? negate(clause) : clause
		}

		// Exact contiguous phrase on the analyzed field (no `.keyword`). The
		// dominant clause type across OOTB policies.
		case 'matchphrase':
		case 'notmatchphrase': {
			if (typeof value !== 'string' || value === '') return false
			const clause: QueryClause = { match_phrase: { [field]: value } }
			return op === 'notmatchphrase' ? negate(clause) : clause
		}

		// Regular expression. Short string form on `.keyword`, case-sensitive —
		// mirrors how the backend authors regexp clauses.
		case 'regexp':
		case 'notregexp': {
			if (typeof value !== 'string' || value === '') return false
			const clause: QueryClause = { regexp: { [keywordSuffix(field)]: value } }
			return op === 'notregexp' ? negate(clause) : clause
		}

		default:
			return false
	}
}
